import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { EffortHeatmapRow } from './EffortHeatmapRow';
import { EffortLegend } from './EffortLegend';
import { TeamRecoveryView } from './TeamRecoveryView';
import { TeamEffortRecoveryView } from './TeamEffortRecoveryView';
import { getOutOfRangeAthleteRows, useTeamEffort } from './useTeamEffort';
import { useKeyboardActivate } from '../../../hooks/useKeyboardActivate';
import { useDetailParam } from '../../../hooks/useDetailParam';
import AthleteListModal from '../AthleteListModal/AthleteListModal';
import MetricHistoryModal from '../MetricHistoryModal/MetricHistoryModal';
import AthleteEffortDrawer from './AthleteEffortDrawer';
import { MetricCellClickParams } from '../MetricHistoryModal/metricHistory.constants';
import PageHelpButton from '../../../shared/PageHelpButton/PageHelpButton';
import styles from './TeamEffortTab.module.scss';

type EffortViewKind = 'sforzo' | 'recovery' | 'combined';

interface TeamEffortTabProps {
  teamId: string;
}

const SUMMARY_HEADERS = ['Sett.', 'Δ%', 'ACWR', 'Mono'];

function SkeletonRow() {
  return (
    <tr>
      <td className={styles.tdAthlete}>
        <div className={styles.skeletonName} />
      </td>
      {Array.from({ length: 7 }, (_, i) => (
        <td key={i} className={styles.tdDay}>
          <div className={styles.skeletonBlock} />
        </td>
      ))}
      {SUMMARY_HEADERS.map((h) => (
        <td key={h} className={styles.tdSummary}>
          <div className={styles.skeletonBlock} />
        </td>
      ))}
    </tr>
  );
}

/**
 * Tab "Sforzo" — vista team: KPI header, heatmap sRPE atleti × 7 giorni
 * (lun–dom), colonne sintesi Sett./Δ%/ACWR/Mono, toggle sRPE|RPE,
 * navigazione settimane. Spec: docs/superpowers/specs/2026-07-26-tab-sforzo-design.md
 */
export function TeamEffortTab({ teamId }: TeamEffortTabProps) {
  const [view, setView] = useState<EffortViewKind>('sforzo');
  const [selectedMetricCell, setSelectedMetricCell] = useState<MetricCellClickParams | null>(null);
  const [selectedAthleteId, setSelectedAthleteId] = useState<string | null>(null);
  const [selectedAthleteName, setSelectedAthleteName] = useState<string | null>(null);
  const [isOutOfRangeModalOpen, setIsOutOfRangeModalOpen] = useState(false);
  const { detailKind, openDetail, closeDetail } = useDetailParam();

  /** Click su una cella storico (sRPE/ACWR/RI/CMJ/HRV/HRrest): salva il payload
   * e apre il dettaglio (crea/aggiorna l'entry di history — fix B4/#18). */
  const handleMetricCellClick = (params: MetricCellClickParams) => {
    setSelectedMetricCell(params);
    openDetail('metric');
  };

  /** Click su una riga atleta: apre il drill-down Sforzo in un drawer laterale
   * (in-place, niente navigazione a un'altra pagina). Wired all'URL come il
   * dettaglio metrica: il Back del browser / Esc / backdrop lo chiudono. */
  const handleSelectAthlete = (athleteId: string, athleteName?: string) => {
    setSelectedAthleteId(athleteId);
    // Nome già noto dalla riga: mostrato subito nel drawer, senza il flash
    // "Atleta" mentre la query effort carica (e senza fallback permanente in
    // caso di errore). `null` se la riga non lo espone → il detail lo recupera.
    setSelectedAthleteName(athleteName ?? null);
    openDetail('effort');
  };
  const {
    week,
    weekDays,
    weekLabel,
    isCurrentWeek,
    mode,
    setMode,
    goPrevWeek,
    goNextWeek,
    goCurrentWeek,
    kpi,
    rows,
    dayEventLabels,
    maxWeekTotal,
    isLoading,
    isError,
    refetch,
  } = useTeamEffort(teamId);

  const fmt = (v: number | null | undefined, digits = 0): string =>
    v == null ? '–' : v.toFixed(digits);

  const isRecovery = view === 'recovery';
  const isCombined = view === 'combined';
  const isSforzo = view === 'sforzo';
  const hasOutOfRange = (kpi?.outOfRangeCount ?? 0) > 0;
  const outOfRangeAthletes = useMemo(() => getOutOfRangeAthleteRows(rows), [rows]);
  const openOutOfRangeModal = hasOutOfRange ? () => setIsOutOfRangeModalOpen(true) : undefined;
  const outOfRangeActivateProps = useKeyboardActivate(openOutOfRangeModal);

  return (
    <div className={styles.root}>
      {/* ── Switch vista + help pagina (help fuori dallo switch esclusivo) ── */}
      <div className={styles.tabHeader}>
        <div className={styles.viewSwitch}>
          <button
            className={`${styles.viewSwitchBtn} ${isSforzo ? styles.viewSwitchBtnActive : ''}`}
            onClick={() => setView('sforzo')}
          >
            Sforzo
          </button>
          <button
            className={`${styles.viewSwitchBtn} ${isRecovery ? styles.viewSwitchBtnActive : ''}`}
            onClick={() => setView('recovery')}
          >
            Recovery
          </button>
          <button
            className={`${styles.viewSwitchBtn} ${isCombined ? styles.viewSwitchBtnActive : ''}`}
            onClick={() => setView('combined')}
          >
            Sforzo + Recovery
          </button>
        </div>
        <PageHelpButton
          pageKey={
            isCombined ? 'page-combined' : isRecovery ? 'page-recovery' : 'page-effort'
          }
        />
      </div>

      {/* ── KPI header (solo vista Sforzo) ── */}
      {isSforzo && (
      <div className={styles.kpiRow}>
        <div className={styles.kpiCard}>
          <div className={styles.kpiLabelRow}>
            <p className={styles.kpiLabel}>Carico medio squadra</p>
          </div>
          <p className={styles.kpiValue}>
            {fmt(kpi?.avgLoad)}
            <span className={styles.kpiUnit}>AU</span>
          </p>
        </div>
        <div className={styles.kpiCard}>
          <div className={styles.kpiLabelRow}>
            <p className={styles.kpiLabel}>ACWR medio</p>
          </div>
          <p className={styles.kpiValue}>{fmt(kpi?.avgAcwr, 2)}</p>
        </div>
        <div
          className={`${styles.kpiCard} ${hasOutOfRange ? styles.kpiCardClickable : ''}`}
          onClick={openOutOfRangeModal}
          {...outOfRangeActivateProps}
        >
          <p className={styles.kpiLabel}>Atleti fuori range</p>
          <p className={`${styles.kpiValue} ${hasOutOfRange ? styles.kpiValueAlert : ''}`}>
            {kpi?.outOfRangeCount ?? '–'}
          </p>
        </div>
        <div className={styles.kpiCard}>
          <div className={styles.kpiLabelRow}>
            <p className={styles.kpiLabel}>Monotonia media</p>
          </div>
          <p className={styles.kpiValue}>{fmt(kpi?.avgMonotony, 1)}</p>
        </div>
      </div>
      )}

      {/* ── Toolbar: navigazione settimana (condivisa) + toggle modalità sforzo ── */}
      <div className={styles.toolbar}>
        <div className={styles.weekNav}>
          <button className={styles.weekNavBtn} onClick={goPrevWeek} title="Settimana precedente">
            <ChevronLeft className={styles.weekNavIcon} />
          </button>
          <p className={styles.weekLabel}>{weekLabel}</p>
          <button
            className={styles.weekNavBtn}
            onClick={goNextWeek}
            disabled={isCurrentWeek}
            title="Settimana successiva"
          >
            <ChevronRight className={styles.weekNavIcon} />
          </button>
          {!isCurrentWeek && (
            <button className={styles.todayBtn} onClick={goCurrentWeek}>
              Settimana corrente
            </button>
          )}
        </div>

        {isSforzo && (
          <div className={styles.modeToggle}>
            <button
              className={`${styles.modeToggleBtn} ${mode === 'srpe' ? styles.modeToggleBtnActive : ''}`}
              onClick={() => setMode('srpe')}
            >
              sRPE (AU)
            </button>
            <button
              className={`${styles.modeToggleBtn} ${mode === 'rpe' ? styles.modeToggleBtnActive : ''}`}
              onClick={() => setMode('rpe')}
            >
              RPE
            </button>
          </div>
        )}
      </div>

      {/* ── Contenuto: heatmap Sforzo, vista Recovery o vista combinata (stessa settimana) ── */}
      {isRecovery ? (
        <TeamRecoveryView
          teamId={teamId}
          weekIso={week}
          weekDays={weekDays}
          onSelectAthlete={handleSelectAthlete}
          onCellClick={handleMetricCellClick}
        />
      ) : isCombined ? (
        <TeamEffortRecoveryView
          teamId={teamId}
          weekIso={week}
          weekDays={weekDays}
          effortRows={rows}
          isEffortLoading={isLoading}
          isEffortError={isError}
          onSelectAthlete={handleSelectAthlete}
        />
      ) : isError ? (
        <div className={styles.stateBox}>
          <p className={styles.stateText}>Impossibile caricare i dati sforzo del team.</p>
          <button className={styles.retryBtn} onClick={() => refetch()}>
            Riprova
          </button>
        </div>
      ) : !isLoading && rows.length === 0 ? (
        <div className={styles.stateBox}>
          <p className={styles.stateText}>Nessun dato sRPE per questa settimana.</p>
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.thAthlete}>Atleta</th>
                {weekDays.map((d, i) => (
                  <th key={d.format('YYYY-MM-DD')} className={styles.thDay}>
                    <span className={styles.thDayName}>{d.format('ddd D')}</span>
                    {dayEventLabels[i] && (
                      <span className={styles.thDayEvent} title={dayEventLabels[i] ?? undefined}>
                        {dayEventLabels[i]}
                      </span>
                    )}
                  </th>
                ))}
                {SUMMARY_HEADERS.map((h) => (
                  <th key={h} className={styles.thSummary}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading
                ? Array.from({ length: 6 }, (_, i) => <SkeletonRow key={i} />)
                : rows.map((row) => (
                    <EffortHeatmapRow
                      key={row.athlete._id}
                      row={row}
                      mode={mode}
                      maxWeekTotal={maxWeekTotal}
                      onSelect={handleSelectAthlete}
                      onCellClick={handleMetricCellClick}
                    />
                  ))}
            </tbody>
          </table>
        </div>
      )}

      {isSforzo && <EffortLegend mode={mode} />}

      <MetricHistoryModal
        isOpen={detailKind === 'metric'}
        onClose={closeDetail}
        athleteId={selectedMetricCell?.athleteId ?? null}
        athleteName={selectedMetricCell?.athleteName ?? null}
        metric={selectedMetricCell?.metric ?? null}
        highlightDate={selectedMetricCell?.highlightDate ?? null}
      />

      <AthleteEffortDrawer
        open={detailKind === 'effort' && selectedAthleteId != null}
        onClose={closeDetail}
        athleteId={selectedAthleteId}
        athleteName={selectedAthleteName}
        teamId={teamId}
      />

      <AthleteListModal
        isOpen={isOutOfRangeModalOpen}
        onClose={() => setIsOutOfRangeModalOpen(false)}
        title="Atleti fuori range"
        emptyMessage="Nessun atleta fuori range questa settimana."
        athletes={outOfRangeAthletes}
      />
    </div>
  );
}

export default TeamEffortTab;
